import * as Sentry from '@sentry/browser';
import type { Breadcrumb, BreadcrumbHint, ErrorEvent } from '@sentry/browser';

// Embedded into every bundled module by sentryVitePlugin (vite.config.mjs), keep both in sync.
// Frames from files without it are third-party code (browser extensions, injected scripts).
export const SENTRY_APPLICATION_KEY = 'databox-webapp';

// property on the native XMLHttpRequest that holds its X-Request-Id: the request service sets it through
// jQuery's `xhrFields`, because the XHR breadcrumb hint has no request headers
export const REQUEST_ID_XHR_FIELD = 'databoxRequestId';

// an event within this many seconds of a failed request gets its id as `lastFailedRequestId`
const LAST_FAILED_REQUEST_MAX_AGE_S = 10;

// network calls that are just noise in breadcrumbs (same list as the Rollbar telemetry transform)
const IGNORED_BREADCRUMB_URLS = [
	'https://api.mixpanel.com/',
	'https://api.segment.io/',
	'https://rs.livesession.io',
];

// does nothing unless the server rendered window.sentryConfig (views/external/sentry.ejs)
export function initSentry(): void {
	const config = window.sentryConfig;
	if (!config || Sentry.getClient()) {
		return;
	}

	Sentry.init({
		dsn: config.dsn,
		environment: config.environment,
		release: config.release,
		sampleRate: 1,
		sendDefaultPii: false,
		// the logger service owns window.onerror/unhandledrejection (shared with Rollbar), so
		// Sentry's own global handlers stay off, otherwise every error is reported twice
		integrations: (defaultIntegrations) => [
			...defaultIntegrations.filter(
				(integration) => !['GlobalHandlers', 'BrowserApiErrors'].includes(integration.name),
			),
			Sentry.thirdPartyErrorFilterIntegration({
				filterKeys: [SENTRY_APPLICATION_KEY],
				// tag only for now, switch to 'drop-error-if-exclusively-contains-third-party-frames'
				// once the tagged errors are confirmed to be noise
				behaviour: 'apply-tag-if-exclusively-contains-third-party-frames',
			}),
		],
		denyUrls: [/^(chrome|moz|safari|safari-web)-extension:\/\//],
		// old-browser noise (e.g. Chrome 88 on TV devices) is dropped by the project's
		// legacy browsers inbound filter (Chrome < 111), so there's no ignoreErrors list
		beforeBreadcrumb(breadcrumb, hint) {
			if (!isRequestBreadcrumb(breadcrumb)) {
				return breadcrumb;
			}

			const url: unknown = breadcrumb.data?.url;
			if (
				typeof url === 'string' &&
				IGNORED_BREADCRUMB_URLS.some((ignoredUrl) => url.startsWith(ignoredUrl))
			) {
				return null;
			}

			const requestId = getBreadcrumbRequestId(breadcrumb, hint);
			if (requestId) {
				breadcrumb.data = { ...breadcrumb.data, requestId };
			}
			return breadcrumb;
		},
		// an error without its own request (e.g. a crash after a failed Ember Data request) gets the id of
		// the request that failed just before it, under a separate tag as the link is only likely
		beforeSend(event, hint) {
			if (!event.tags?.requestId) {
				const lastFailedRequestId = findLastFailedRequestId(event);
				if (lastFailedRequestId) {
					event.tags = { ...event.tags, lastFailedRequestId };
				}
			}
			// tag only for now (like the third-party filter above), drop once the tagged events are
			// confirmed to be noise; the same error on an untranslated page is still reported as is
			if (isDomMoveError(hint.originalException) && isPageTranslated()) {
				event.tags = { ...event.tags, dom_translated: 'yes' };
				event.level = 'warning';
			}
			return event;
		},
		initialScope: {
			tags: { service: 'webapp' },
			user: config.userId ? { id: String(config.userId) } : undefined,
		},
	});
}

// context logged with an error: any object (an uncaught error passes the Error itself); the logger adds
// spaceId / activeToken to it, a failed request brings its requestId
export interface LogData {
	[key: string]: unknown;
	stack?: string;
	spaceId?: number;
	activeToken?: string | null;
	requestId?: string;
}

export function reportToSentry(msg: string, extradata: LogData, level: Sentry.SeverityLevel): void {
	// requestId is a tag so it's searchable, under the same name analytics-api uses
	const tags: Record<string, string> = {};
	if (extradata.spaceId) {
		tags.spaceId = String(extradata.spaceId);
	}
	if (typeof extradata.requestId === 'string' && extradata.requestId) {
		tags.requestId = extradata.requestId;
	}

	if (extradata instanceof Error) {
		// uncaught errors and rejections pass the original error object
		Sentry.captureException(extradata, { level, tags });
	} else {
		Sentry.captureMessage(msg, { level, tags, extra: extradata });
	}
}

// Glimmer removing or inserting a node that something outside it has moved
function isDomMoveError(error: unknown): boolean {
	return (
		error instanceof DOMException &&
		error.name === 'NotFoundError' &&
		/removeChild|insertBefore/.test(error.message)
	);
}

// a page translator rewrites text nodes under Glimmer, which then fails to update them
function isPageTranslated(): boolean {
	const html = document.documentElement;
	return (
		// Google Translate (Chrome's built-in one too)
		html.classList.contains('translated-ltr') ||
		html.classList.contains('translated-rtl') ||
		// its translated-text wrappers; matched by style, as user HTML in blocks may have a plain <font>
		!!document.querySelector('font[style*="vertical-align: inherit"]') ||
		// Monica translator extension
		!!document.querySelector('monica-translate-translatable')
	);
}

function isRequestBreadcrumb(breadcrumb: Breadcrumb): boolean {
	return breadcrumb.category === 'xhr' || breadcrumb.category === 'fetch';
}

// X-Request-Id of an xhr/fetch breadcrumb's request
function getBreadcrumbRequestId(breadcrumb: Breadcrumb, hint?: BreadcrumbHint): string | undefined {
	if (breadcrumb.category === 'xhr') {
		const requestId: unknown = (hint?.xhr as Record<string, unknown> | undefined)?.[
			REQUEST_ID_XHR_FIELD
		];
		return typeof requestId === 'string' && requestId ? requestId : undefined;
	}

	// the fetch(input, init) arguments; Ember Data passes its headers as a plain object in init
	const [input, init] = (hint?.input ?? []) as [unknown, RequestInit | undefined];
	const headers = init?.headers ?? (input instanceof Request ? input.headers : undefined);
	if (!headers) {
		return undefined;
	}
	try {
		return new Headers(headers).get('X-Request-Id') || undefined;
	} catch {
		return undefined; // headers fetch itself would reject
	}
}

// id of the newest request that failed (HTTP error, network error, timeout) shortly before the event
function findLastFailedRequestId(event: ErrorEvent): string | undefined {
	const breadcrumbs = event.breadcrumbs ?? [];
	const eventTimestamp = event.timestamp ?? Date.now() / 1000;

	for (let i = breadcrumbs.length - 1; i >= 0; i--) {
		const breadcrumb = breadcrumbs[i]!;
		if (
			breadcrumb.timestamp &&
			eventTimestamp - breadcrumb.timestamp > LAST_FAILED_REQUEST_MAX_AGE_S
		) {
			return undefined; // breadcrumbs are oldest first, so the rest are older still
		}

		const requestId: unknown = breadcrumb.data?.requestId;
		const statusCode: unknown = breadcrumb.data?.status_code;
		const isFailed =
			breadcrumb.level === 'error' ||
			statusCode === 0 || // XHR network error, timeout or abort
			(typeof statusCode === 'number' && statusCode >= 400);

		if (isRequestBreadcrumb(breadcrumb) && isFailed && typeof requestId === 'string') {
			return requestId;
		}
	}
	return undefined;
}
